import { useEffect } from 'react';
import { tracking } from 'App/Tracking';
import { useTrackServerside } from 'App/Tracking/serverside';
import { EventName, OnboardingSupportOption } from '__generated__/globalTypes';

export type DismissMethod = 'close' | 'not-sure';

/**
 * Where the member entered the modal from: it opened by itself in the
 * onboarding flow, or they reopened it from the checklist.
 */
export type WelcomeModalEntryPoint = 'onboarding-flow' | 'checklist';

/**
 * Tracking for the welcome modal.
 *
 * Impressions, selections, call-to-action clicks and dismissals are sent
 * twice: client-side reaches Amplitude, server-side reaches the datalake.
 * Neither pipeline reaches the other, and these are the only record of what a
 * member chose, so both are needed. Back clicks are the exception and stay
 * client-side only.
 *
 * `shown` lets callers invoke this before the checks that decide whether the
 * modal renders, and the effect depends on it alone so re-renders cannot fire a
 * second impression while the modal stays open.
 *
 * Every event carries `entryPoint`, so a reopen from the checklist can be told
 * apart from the first, automatic open.
 */
export const useWelcomeModalTracking = ({
  shown,
  options,
  entryPoint,
}: {
  shown: boolean;
  options: OnboardingSupportOption[];
  entryPoint: WelcomeModalEntryPoint;
}) => {
  const trackServerside = useTrackServerside();

  useEffect(() => {
    if (!shown) {
      return;
    }

    tracking.track('welcome-modal-shown', { options, entryPoint });
    void trackServerside({
      eventName: EventName.WELCOME_MODAL_SHOWN,
      eventProperties: { options, entryPoint },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);

  const trackOptionClicked = (option: OnboardingSupportOption) => {
    tracking.track('welcome-modal-option-clicked', { option, entryPoint });
    void trackServerside({
      eventName: EventName.WELCOME_MODAL_OPTION_CLICKED,
      eventProperties: { option, entryPoint },
    });
  };

  const trackCtaClicked = (
    option: OnboardingSupportOption,
    destination: string,
  ) => {
    tracking.track('welcome-modal-cta-clicked', {
      option,
      destination,
      entryPoint,
    });
    void trackServerside({
      eventName: EventName.WELCOME_MODAL_CTA_CLICKED,
      eventProperties: { option, destination, entryPoint },
    });
  };

  const trackDismissed = (method: DismissMethod) => {
    tracking.track('welcome-modal-dismissed', { method, entryPoint });
    void trackServerside({
      eventName: EventName.WELCOME_MODAL_DISMISSED,
      eventProperties: { method, entryPoint },
    });
  };

  /** Client only: navigating back is funnel detail, not an outcome to persist. */
  const trackBackClicked = (option: OnboardingSupportOption) => {
    tracking.track('welcome-modal-back-clicked', { option, entryPoint });
  };

  return {
    trackOptionClicked,
    trackCtaClicked,
    trackDismissed,
    trackBackClicked,
  };
};
